import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { format, subDays, setHours, setMinutes, setSeconds } from 'date-fns';
import * as Yup from 'yup';
import { isEmpty } from 'lodash';

import { toast } from 'react-toastify';
import api from '~/services/api';
import { isNull, isArray } from 'lodash';

import { t, parseDateString } from '~/helpers';
import { Report, ListTransfer } from '~/components';
import { DateTime, Autocomplete, RadioGroup } from '~/components/Form';

export default function RelListagemImpedimento() {
  const [open, setOpen] = useState(true);
  const [organ, setOrgan] = useState({ orga_codigo: '', orga_nome: '' });
  const [organDisable, setOrganDisable] = useState(false);

  const [units, setUnits] = useState([]);

  const { glousuariologin } = useSelector((state) => state.user.profile);

  const cells = useMemo(
    () => [
      { id: 'unid_nome', label: 'Unidade' },
      { id: 'unid_codigo', label: 'Código' },
    ],
    []
  );

  const datahorainicial = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(subDays(new Date(), 7), 0), 0), 0),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const datahorafinal = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(new Date(), 23), 59), 59),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const handleCloseReport = useCallback(() => {
    setOpen(false);
  }, []);

  const handleValidate = useCallback(
    async (data) => {
      try {
        const schema = Yup.object().shape({
          datahorainicial: Yup.date()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .transform((_, value) => parseDateString(value))
            .required(t('s.geral.validacao.campoobrigatorio')),
          datahorafinal: Yup.date()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .transform((_, value) => parseDateString(value))
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(
              datahorafinal,
              `A data não pode ser superior a ${datahorafinal}`
            )
            .min(
              Yup.ref('datahorainicial'),
              'A data não pode ser inferior a Data inicial'
            ),
          orgao: Yup.string().required(t('s.geral.validacao.campoobrigatorio')),
        });

        await schema.validate(data, { abortEarly: false });

        if (isEmpty(units))
          throw 'Por favor, selecione ao menos uma unidade antes de enviar.';
      } catch (err) {
        const form = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            form[error.path] = error.message.replace(`${error.path} `, '');
          });

          throw { form };
        } else {
          throw err;
        }
      }
    },
    [units, datahorafinal]
  );

  const handleValidOrgaos = useCallback(async () => {
    try {
      const { data } = await api.get('orgaos');

      if (data.items.length == 1) {
        let tempOrgan = data.items[0];
        setOrgan(tempOrgan);
        setOrganDisable(true);
      } else if (data.items.length == 0) {
        setOrgan({ orga_codigo: '', orga_nome: '' });
        setOrganDisable(true);
      } else {
        setOrganDisable(false);
      }
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    }
  }, []);

  const handleChangeOrgan = useCallback((value) => {
    if (isNull(value.orga_nome)) setOrgan({ orga_codigo: '', orga_nome: '' });
    else {
      setOrgan(value);
    }
  }, []);

  const onSerializeData = useCallback(
    (data) => {
      let codigos = units.map((unity) => unity.unid_codigo);

      return {
        ...data,
        codigos,
        orgao: data.orgao ? data.orgao + '' : '',
        orgaonome: organ.orga_nome,
        usuarioAleatorio: glousuariologin,
      };
    },
    [organ, units]
  );

  useEffect(() => {
    setUnits([]);
  }, [organ]);

  useEffect(() => {
    handleValidOrgaos();
  }, []);

  return (
    <Report
      open={open}
      onClose={handleCloseReport}
      serialize={onSerializeData}
      validate={handleValidate}
      requestType="POST"
      title="Listagem de Impedimento"
      relatorio="rellistagemimpedimento"
      maxWidth="md"
      initials={{
        datahorainicial,
        datahorafinal,
        tipo: 'pdf',
        orgao: organ.orga_codigo,
      }}
    >
      <DateTime
        time
        disableFuture
        label="Data Inicial"
        name="datahorainicial"
      />

      <DateTime
        time
        disableFuture
        label="Data Final"
        name="datahorafinal"
        maxDateMessage=""
      />

      <Autocomplete
        includeInputInList
        disabledItemsFocusable
        disableClearable={!organ.orga_nome}
        name="orgao"
        label="orga_nome"
        title="Órgão"
        url="orgaos"
        unique="orga_codigo"
        inputValue={organ.orga_nome}
        disabled={organDisable}
        getValue={handleChangeOrgan}
        onInputChange={(event, value) => {
          let obj = {
            ...organ,
            orga_nome: value,
          };
          setOrgan(obj);
        }}
      />

      <ListTransfer
        search
        grid
        cells={cells}
        url="unidades"
        label="unid_nome"
        unique="unid_codigo"
        leftTitle="Unidades disponíveis"
        rightTitle="Unidades selecionadas"
        onCallback={(value) => setUnits(value)}
        maxWidth={38}
        maxHeight={200}
        reset={isEmpty(units)}
        query={{
          wherefield: ['orga_codigo'],
          valuewherefield: [organ.orga_codigo],
          typewherefield: ['EQUALS'],
        }}
        showCount
      />

      <RadioGroup
        row
        name="tipo"
        label="Formato"
        options={[
          { label: 'PDF', value: 'pdf' },
          { label: 'Excel', value: 'xls' },
        ]}
        defaultValue="pdf"
      />
    </Report>
  );
}
